import { CLIENT_ID } from "../constants";

export interface PickerSelectedItem {
  fileId: string;
  name: string;
  mimeType: string;
  thumbnailLink?: string;
}

export interface PickerSelectionResult {
  files: PickerSelectedItem[];
  folderIds: string[];
}

export type BulkImportIntent =
  | { type: "invalid" }
  | { type: "picker"; presetFileIds?: string[]; detectedFolderLinks: boolean };

declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

const PICKER_API_KEY = (import.meta as any).env?.VITE_GOOGLE_API_KEY || "";

export const loadPickerApi = async (): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    if (window.gapi?.load) {
      window.gapi.load("picker", { callback: resolve, onerror: reject });
      return;
    }
    reject(new Error("GAPI not loaded."));
  });
};

const buildUnifiedMediaFolderView = (googlePicker: any) =>
  new googlePicker.DocsView(googlePicker.ViewId.DOCS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(true)
    .setMode(googlePicker.DocsViewMode.LIST)
    .setMimeTypes(
      [
        "application/vnd.google-apps.folder",
        "image/jpeg",
        "image/png",
        "image/webp",
        "image/gif",
        "image/heic",
        "image/heif",
        "image/bmp",
        "image/tiff",
        "video/mp4",
        "video/quicktime",
        "video/webm",
        "video/x-matroska",
        "video/x-msvideo",
      ].join(","),
    );

const isFolderDoc = (doc: any): boolean => {
  const mimeType = (doc?.mimeType || "").toString();
  const type = (doc?.type || "").toString().toLowerCase();
  return (
    mimeType === "application/vnd.google-apps.folder" ||
    type === "folder" ||
    type.includes("folder")
  );
};

const getDocMimeType = (doc: any): string => {
  const mimeType = (doc?.mimeType || "").toString();
  if (mimeType) return mimeType;
  const type = (doc?.type || "").toString().toLowerCase();
  if (type.includes("image") || type === "photo") return "image/*";
  if (type.includes("video")) return "video/*";
  return "";
};

const getDocField = (
  doc: any,
  key: string,
  fallbackKeys: string[] = [],
): any => {
  if (!doc || typeof doc !== "object") return undefined;
  if (doc[key] !== undefined && doc[key] !== null) return doc[key];
  for (const fallbackKey of fallbackKeys) {
    if (doc[fallbackKey] !== undefined && doc[fallbackKey] !== null) {
      return doc[fallbackKey];
    }
  }
  return undefined;
};

export const normalizePickedDocs = (docs: any[]): PickerSelectionResult => {
  const selected: PickerSelectedItem[] = [];
  const folderIds = new Set<string>();

  for (const raw of docs || []) {
    const doc = raw || {};
    const docId = String(
      getDocField(doc, "id", ["documentId", "resourceKey"]) || "",
    ).trim();
    const name = String(
      getDocField(doc, "name", ["title"]) || "",
    ).trim();

    if (isFolderDoc(doc)) {
      if (docId) folderIds.add(docId);
      continue;
    }

    const mimeType = getDocMimeType(doc);
    if (!mimeType.startsWith("image/") && !mimeType.startsWith("video/")) {
      continue;
    }

    if (!docId) continue;
    selected.push({
      fileId: docId,
      name: name || docId,
      mimeType,
      thumbnailLink:
        getDocField(doc, "thumbnailLink", []) ||
        doc?.thumbnails?.[0]?.url ||
        undefined,
    });
  }

  return { files: selected, folderIds: Array.from(folderIds) };
};

export const openDrivePicker = async ({
  accessToken,
  presetFileIds,
}: {
  accessToken: string;
  presetFileIds?: string[];
}): Promise<PickerSelectionResult> => {
  await loadPickerApi();

  return new Promise((resolve, reject) => {
    const googlePicker = window.google.picker;
    const builder = new googlePicker.PickerBuilder()
      .enableFeature(googlePicker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(accessToken)
      .setDeveloperKey(PICKER_API_KEY)
      .setAppId(CLIENT_ID.split("-")[0])
      .setOrigin(window.location.origin)
      .setTitle("Pick files or one folder to import all photos/videos")
      .setCallback((data: any) => {
        if (data.action === googlePicker.Action.CANCEL) {
          resolve({ files: [], folderIds: [] });
          return;
        }
        if (data.action !== googlePicker.Action.PICKED) {
          return;
        }
        resolve(normalizePickedDocs(data.docs || []));
      });

    if (presetFileIds && presetFileIds.length > 0) {
      const idView = new googlePicker.DocsView(googlePicker.ViewId.DOCS)
        .setMode(googlePicker.DocsViewMode.LIST)
        .setFileIds(presetFileIds.slice(0, 500).join(","));
      builder.addView(idView);
    }

    const view = buildUnifiedMediaFolderView(googlePicker);
    builder.addView(view);

    try {
      const picker = builder.build();
      picker.setVisible(true);
    } catch (error) {
      reject(error);
    }
  });
};


export const parseDriveLinksAndIds = (
  input: string,
): { fileIds: string[]; folderIds: string[] } => {
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const fileIds = new Set<string>();
  const folderIds = new Set<string>();

  const pushIfId = (value: string | undefined, target: Set<string>) => {
    if (!value) return;
    const trimmed = value.trim();
    if (/^[a-zA-Z0-9_-]{10,}$/.test(trimmed)) {
      target.add(trimmed);
    }
  };

  for (const line of lines) {
    pushIfId(line, fileIds);

    const filePathMatch = line.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    pushIfId(filePathMatch?.[1], fileIds);

    const openIdMatch = line.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    pushIfId(openIdMatch?.[1], fileIds);

    const ucMatch = line.match(/\/uc\?id=([a-zA-Z0-9_-]+)/);
    pushIfId(ucMatch?.[1], fileIds);

    const folderMatch = line.match(/\/drive\/folders\/([a-zA-Z0-9_-]+)/);
    pushIfId(folderMatch?.[1], folderIds);
  }

  return {
    fileIds: Array.from(fileIds),
    folderIds: Array.from(folderIds),
  };
};

export const resolveBulkImportIntent = (parsed: {
  fileIds: string[];
  folderIds: string[];
}): BulkImportIntent => {
  if (parsed.fileIds.length === 0 && parsed.folderIds.length === 0) {
    return { type: "invalid" };
  }
  if (parsed.fileIds.length > 0) {
    return {
      type: "picker",
      presetFileIds: parsed.fileIds,
      detectedFolderLinks: parsed.folderIds.length > 0,
    };
  }
  return { type: "picker", detectedFolderLinks: true };
};
